import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const checkAuth = useCallback(async () => {
    // Skip auth check if we're on the callback page - let the callback handle auth
    if (window.location.pathname === '/auth/callback' || window.location.pathname === '/auth-callback.html') {
      setLoading(false);
      return;
    }
    
    try {
      const userData = await api.getMe();
      setUser(userData);
      setIsAuthenticated(true);
    } catch (error) {
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await api.login(email, password);
    setUser(response.user);
    setIsAuthenticated(true);
    // Store token for API calls
    sessionStorage.setItem('auth_token', response.access_token);
    return response;
  };

  const signup = async (email, password, name) => {
    const response = await api.signup(email, password, name);
    setUser(response.user);
    setIsAuthenticated(true);
    sessionStorage.setItem('auth_token', response.access_token);
    return response;
  };

  const googleAuth = async (sessionId) => {
    const response = await api.googleAuth(sessionId);
    setUser(response.user);
    setIsAuthenticated(true);
    sessionStorage.setItem('auth_token', response.access_token);
    return response;
  };

  const logout = async () => {
    try {
      await api.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    setIsAuthenticated(false);
    sessionStorage.removeItem('auth_token');
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    signup,
    googleAuth,
    logout,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
